<template>
  <form class="nx-form" :class="{ 'nx-active': active }" :data-vv-scope="scope" v-on="$listeners">
    <slot name="default" v-bind="{ draft, form, formErrors }"></slot>
  </form>
</template>

<script>
import isEqual from 'lodash/isEqual';
import groupBy from 'lodash/groupBy';
import isNil from 'lodash/isNil';

/**
 * Form Component
 *
 * By default each Form has their own validation scope.
 * If you need to inherit the parent validation scope in child forms (ex: Nested forms)
 * you must use the FormScope component
 *
 * @property {string}  title         The title/legend of the form
 * @property {Object}  [value]       (Optional) The value of the form.
 * @property {string}  scope         (Optional) Scope the fields in the form (ex: $content.{field})
 *
 * @fires Form#input                 The changes made in the form
 * @fires Form#form                  The form global validation flags (valid, dirty, pristine, active...)
 * @fires Form#fields                The validation flags for each form flag (valid, dirty, pristine, active...)
 * @fires Form#errors                The form errors
 */
const VALIDATION_MODE_DEFAULT = 'default';
const VALIDATION_MODE_EAGER = 'eager';

export default {
  name: 'nx-form',
  $_veeValidate: {
    validator: 'new', // give me a new vee-validate instance each time.
  },
  provide() {
    return {
      $parentForm: this,
    };
  },
  components: {},
  props: {
    title: {
      type: String,
      required: false,
    },
    value: {
      type: Object,
      default: () => {},
    },
    scope: {
      type: String,
      required: false,
      default: null,
    },
    disabled: {
      type: Boolean,
    },
    validationMode: {
      type: String,
      default: VALIDATION_MODE_DEFAULT,
    },
  },
  computed: {
    active() {
      return Object.values(this.formFields).some(flags => flags.focused);
    },
    draft() {
      return this.value;
    },
    /**
     * Get form status
     * @returns {Object}
     */
    form() {
      // Indicates that the form has been manipulated.
      const pristine = Object.keys(this.formFields).every(key => this.formFields[key].pristine);
      // Indicates that the form has been validated at least once and that it failed the validation.
      const valid = Object.keys(this.formFields).every(key => this.formFields[key].valid);
      // Indicates that the form has not been touched nor focused.
      const touched = Object.keys(this.formFields).some(key => this.formFields[key].touched);
      // Indicates that the form has been validated
      const validated = Object.keys(this.formFields).every(key => this.formFields[key].validated);
      // Indicates that the form is valid for publish
      const validForPublish = Object.keys(this.formFields).every((key) => {
        // validForPublish is a custom vee-validate property, sometimes is nullable while the form component is initializating
        // skip the undefined or null values as ok
        const value = this.formFields[key].validForPublish;
        if (isNil(value)) {
          return true;
        }
        return value;
      });
      return {
        active: this.active,
        pristine,
        dirty: !pristine,
        valid,
        validForPublish,
        invalid: !valid,
        touched,
        untouched: !touched,
        validated,
      };
    },
    /**
     * Get local form errors
     * @returns {Array}
     */
    formErrors() {
      if (!this.errors) {
        return [];
      }
      if (!this.scope) {
        return this.errors.items;
      }
      return this.getScopeErrors(this.scope);
    },
    formErrorsByScope() {
      if (!this.errors) {
        return [];
      }
      return groupBy(this.errors.items, 'scope');
    },
    /**
     * Get local form fields
     * @returns {Object}
     */
    formFields() {
      return Object.keys(this.fields).reduce((result, field) => {
        if (field.startsWith('$')) {
          return {
            ...result,
            ...Object.keys(this.fields[field]).reduce((scopeFields, scopeField) => ({
              ...scopeFields,
              [`${field}/${scopeField}`]: this.fields[field][scopeField],
            }), {}),
          };
        }
        return {
          ...result,
          [field]: this.fields[field],
        };
      }, {});
    },
    scopes() {
      return Object.keys(this.fields)
        .filter(field => field.startsWith('$'))
        .map(field => field.slice(1));
    },
  },
  methods: {
    getScopeErrors(scope) {
      return this.errors.items.filter(error => error.scope === scope);
    },
    getScopeFields(scope) {
      const fields = this.fields[`$${scope}`];
      if (fields === undefined) {
        return {};
      }
      return fields;
    },
    resetFields() {
      this.$validator.fields.items.forEach((field) => {
        let name = field.name;
        if (field.scope) {
          name = `${field.scope}.${name}`;
        }
        this.$validator.flag(name, {
          touched: false,
          dirty: false,
        });
      });
    },
    updateField(field, value, ...args) {
      console.log('%c[Form] updateField', 'color:darkorange', field, value);
      if (this.value
        && this.value[field]
        && isEqual(this.value[field], value)) {
        // Nothing to update
        return;
      }
      this.$emit('input', field, value, ...args);
    },
    validate() {
      this.$validator.validate();
    },
    validateScope(scope) {
      this.$validator.validateAll(scope);
    },
    clearErrors() {
      this.scopes.forEach(scope => this.$validator.errors.clear(scope));
    },
  },
  watch: {
    form(value, oldValue) {
      if (isEqual(value, oldValue)) {
        return;
      }
      this.$emit('form', value);
    },
    formFields: {
      handler(value) {
        this.$emit('fields', value);
      },
      deep: true,
    },
    formErrors(value) {
      this.$emit('errors', value);
    },

  },
  mounted() {
    if (this.validationMode === VALIDATION_MODE_EAGER) {
      this.validate();
    }
  },
};
</script>
